import { useState } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';
import { api } from '@/lib/api';
import {
  ChevronLeft,
  Package,
  Settings,
  Users,
  BarChart3,
  Info,
  CheckCircle2,
  XCircle,
  AlertCircle,
  Power,
  Trash2,
} from 'lucide-react';

type Tab = 'overview' | 'bobs' | 'config' | 'analytics' | 'settings';

interface BoB {
  id: string;
  name: string;
  description: string;
  category: string;
  is_enabled: boolean;
  last_used: string | null;
}

export function InstanceDetail() {
  const { instanceId } = useParams<{ instanceId: string }>();
  const navigate = useNavigate();
  const [activeTab, setActiveTab] = useState<Tab>('overview');

  // TODO: Replace with actual API call when backend is ready
  const { data, isLoading, error } = useQuery({
    queryKey: ['instance-detail', instanceId],
    queryFn: async () => {
      // Mock data for now - will be replaced with api.getInstanceDetail(instanceId)
      return {
        instance: {
          id: instanceId,
          name: 'My Odoo Instance',
          odoo_url: 'https://mycompany.odoo.com',
          status: 'active',
          health_status: 'healthy' as const,
          tier: 'pro',
          created_at: '2026-01-01T00:00:00Z',
        },
        bobs: [] as BoB[],
      };
    },
  });

  if (isLoading) {
    return (
      <div className="flex items-center justify-center h-96">
        <div className="text-slate-400">Loading instance details...</div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="flex items-center justify-center h-96">
        <div className="text-red-400">Error loading instance: {(error as Error).message}</div>
      </div>
    );
  }

  const instance = data?.instance;
  const bobs = data?.bobs || [];

  if (!instance) {
    return (
      <div className="flex items-center justify-center h-96">
        <div className="text-slate-400">Instance not found</div>
      </div>
    );
  }

  const getHealthIcon = (status: typeof instance.health_status) => {
    switch (status) {
      case 'healthy':
        return <CheckCircle2 className="h-5 w-5 text-emerald-400" />;
      case 'degraded':
        return <AlertCircle className="h-5 w-5 text-yellow-400" />;
      case 'down':
        return <XCircle className="h-5 w-5 text-red-400" />;
    }
  };

  const tabs = [
    { id: 'overview' as Tab, label: 'Overview', icon: Info },
    { id: 'bobs' as Tab, label: 'BoBs', icon: Package },
    { id: 'config' as Tab, label: 'Configuration', icon: Settings },
    { id: 'analytics' as Tab, label: 'Analytics', icon: BarChart3 },
    { id: 'settings' as Tab, label: 'Settings', icon: Settings },
  ];

  return (
    <div className="space-y-6">
      {/* Header */}
      <div>
        <button
          onClick={() => navigate('/client/dashboard')}
          className="flex items-center gap-2 text-sm text-slate-400 hover:text-slate-300 transition-colors mb-4"
        >
          <ChevronLeft className="h-4 w-4" />
          Back to Dashboard
        </button>
        <div className="flex items-start justify-between">
          <div>
            <h1 className="text-3xl font-bold text-slate-100">{instance.name}</h1>
            <p className="mt-1 text-sm text-slate-400">{instance.odoo_url}</p>
          </div>
          <div className="flex items-center gap-2">
            {getHealthIcon(instance.health_status)}
            <span className="text-sm font-medium text-slate-300 capitalize">
              {instance.health_status}
            </span>
          </div>
        </div>
      </div>

      {/* Tabs */}
      <div className="border-b border-slate-700">
        <nav className="flex space-x-8">
          {tabs.map((tab) => {
            const Icon = tab.icon;
            return (
              <button
                key={tab.id}
                onClick={() => setActiveTab(tab.id)}
                className={`flex items-center gap-2 py-4 px-1 border-b-2 font-medium text-sm transition-colors ${
                  activeTab === tab.id
                    ? 'border-blue-500 text-blue-400'
                    : 'border-transparent text-slate-400 hover:text-slate-300'
                }`}
              >
                <Icon className="h-5 w-5" />
                {tab.label}
              </button>
            );
          })}
        </nav>
      </div>

      {/* Tab Content */}
      <div className="bg-slate-800/50 backdrop-blur-sm rounded-lg border border-slate-700 p-6">
        {/* Overview Tab */}
        {activeTab === 'overview' && (
          <div className="space-y-6">
            <h3 className="text-xl font-semibold text-slate-100">Instance Overview</h3>

            <div className="grid gap-4 md:grid-cols-2">
              <div className="space-y-3">
                <div className="flex justify-between">
                  <span className="text-slate-400">Status</span>
                  <span className="text-slate-100 font-medium capitalize">{instance.status}</span>
                </div>
                <div className="flex justify-between">
                  <span className="text-slate-400">Health</span>
                  <span className="text-emerald-400 font-medium capitalize">{instance.health_status}</span>
                </div>
                <div className="flex justify-between">
                  <span className="text-slate-400">Tier</span>
                  <span className="inline-flex items-center px-2 py-0.5 rounded text-xs font-medium bg-emerald-500/10 text-emerald-400 border border-emerald-500/20 capitalize">
                    {instance.tier}
                  </span>
                </div>
              </div>

              <div className="space-y-3">
                <div className="flex justify-between">
                  <span className="text-slate-400">Active BoBs</span>
                  <span className="text-slate-100 font-medium">
                    {bobs.filter(b => b.is_enabled).length} / {bobs.length}
                  </span>
                </div>
                <div className="flex justify-between">
                  <span className="text-slate-400">Created</span>
                  <span className="text-slate-100 font-medium">
                    {new Date(instance.created_at).toLocaleDateString()}
                  </span>
                </div>
                <div className="flex justify-between">
                  <span className="text-slate-400">URL</span>
                  <a
                    href={instance.odoo_url}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="text-blue-400 hover:text-blue-300 font-medium"
                  >
                    Open →
                  </a>
                </div>
              </div>
            </div>

            <div className="pt-4 border-t border-slate-700">
              <h4 className="text-lg font-semibold text-slate-100 mb-4">Quick Actions</h4>
              <div className="flex gap-3">
                <button className="flex items-center gap-2 px-4 py-2 rounded-lg bg-emerald-500/10 text-emerald-400 border border-emerald-500/20 hover:bg-emerald-500/20 transition-colors">
                  <Power className="h-4 w-4" />
                  Restart Instance
                </button>
                <button className="flex items-center gap-2 px-4 py-2 rounded-lg bg-slate-700 text-slate-300 border border-slate-600 hover:bg-slate-600 transition-colors">
                  <Package className="h-4 w-4" />
                  Manage BoBs
                </button>
              </div>
            </div>
          </div>
        )}

        {/* BoBs Tab */}
        {activeTab === 'bobs' && (
          <div className="space-y-6">
            <div className="flex items-center justify-between">
              <h3 className="text-xl font-semibold text-slate-100">Installed BoBs</h3>
              <button className="px-4 py-2 rounded-lg bg-blue-500 hover:bg-blue-600 text-white font-medium transition-colors">
                + Add BoB
              </button>
            </div>

            {bobs.length === 0 ? (
              <div className="text-center py-12">
                <Package className="mx-auto h-12 w-12 text-slate-600" />
                <p className="mt-2 text-slate-400">No BoBs installed yet</p>
                <button className="mt-4 px-4 py-2 rounded-lg bg-blue-500 hover:bg-blue-600 text-white">
                  Browse BoB Marketplace
                </button>
              </div>
            ) : (
              <div className="space-y-3">
                {bobs.map((bob) => (
                  <div
                    key={bob.id}
                    className="flex items-center justify-between p-4 bg-slate-700/30 rounded-lg border border-slate-600"
                  >
                    <div className="flex-1">
                      <h4 className="font-medium text-slate-100">{bob.name}</h4>
                      <p className="text-sm text-slate-400">{bob.description}</p>
                      <div className="mt-2 flex items-center gap-2">
                        <span className="text-xs px-2 py-0.5 rounded bg-slate-600 text-slate-300">
                          {bob.category}
                        </span>
                        {bob.last_used && (
                          <span className="text-xs text-slate-500">
                            Last used: {new Date(bob.last_used).toLocaleDateString()}
                          </span>
                        )}
                      </div>
                    </div>
                    <label className="relative inline-flex items-center cursor-pointer">
                      <input
                        type="checkbox"
                        checked={bob.is_enabled}
                        onChange={() => {
                          // TODO: Implement toggle BoB API call
                        }}
                        className="sr-only peer"
                      />
                      <div className="w-11 h-6 bg-slate-600 peer-focus:outline-none rounded-full peer peer-checked:after:translate-x-full peer-checked:after:border-white after:content-[''] after:absolute after:top-[2px] after:left-[2px] after:bg-white after:rounded-full after:h-5 after:w-5 after:transition-all peer-checked:bg-emerald-500"></div>
                    </label>
                  </div>
                ))}
              </div>
            )}
          </div>
        )}

        {/* Configuration Tab */}
        {activeTab === 'config' && (
          <div className="space-y-6">
            <h3 className="text-xl font-semibold text-slate-100">Configuration</h3>
            <p className="text-slate-400">Instance configuration settings will be displayed here.</p>
          </div>
        )}

        {/* Analytics Tab */}
        {activeTab === 'analytics' && (
          <div className="space-y-6">
            <h3 className="text-xl font-semibold text-slate-100">Analytics</h3>
            <p className="text-slate-400">Usage analytics and metrics will be displayed here.</p>
          </div>
        )}

        {/* Settings Tab */}
        {activeTab === 'settings' && (
          <div className="space-y-6">
            <h3 className="text-xl font-semibold text-slate-100">Instance Settings</h3>

            <div className="space-y-4">
              <div>
                <label className="block text-sm font-medium text-slate-300 mb-2">
                  Instance Name
                </label>
                <input
                  type="text"
                  defaultValue={instance.name}
                  className="w-full px-4 py-2 bg-slate-700 border border-slate-600 rounded-lg text-slate-100 focus:outline-none focus:border-blue-500"
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-slate-300 mb-2">
                  Odoo URL
                </label>
                <input
                  type="url"
                  defaultValue={instance.odoo_url}
                  className="w-full px-4 py-2 bg-slate-700 border border-slate-600 rounded-lg text-slate-100 focus:outline-none focus:border-blue-500"
                />
              </div>

              <div className="pt-4 border-t border-slate-700">
                <h4 className="text-lg font-semibold text-red-400 mb-4">Danger Zone</h4>
                <button className="flex items-center gap-2 px-4 py-2 rounded-lg bg-red-500/10 text-red-400 border border-red-500/20 hover:bg-red-500/20 transition-colors">
                  <Trash2 className="h-4 w-4" />
                  Delete Instance
                </button>
                <p className="mt-2 text-xs text-slate-500">
                  This action cannot be undone. All data will be permanently deleted.
                </p>
              </div>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}
